import axios, {
  AxiosError,
  type AxiosResponseHeaders,
  type AxiosResponseTransformer,
  type InternalAxiosRequestConfig,
  type RawAxiosResponseHeaders,
} from 'axios';

import { handleAxiosError } from '../errors/handleAxiosError';
import {
  applyDuplicateRequestCancellation,
  releaseInFlightRequest,
} from '../network/duplicateRequestGuard';
import {
  hasRecentNetworkFailures,
  recordNetworkFailure,
  recordNetworkSuccess,
} from '../network/networkState';
import {
  getTransportRetryDelay,
  shouldRecordNetworkFailure,
  shouldRetryTransportRequest,
} from '../network/retryConfig';
import {
  type AxiosFetchRequestConfig,
  readRequestMetadata,
  toAxiosConfigWithExtensions,
  withRequestMetadata,
  withUpdatedRetryMetadata,
} from '../network/types';

const REQUEST_TIMEOUT_MS = process.env['NEXT_PUBLIC_REQUEST_TIMEOUT_MS'];

/**
 * Fallback request timeout, used when `NEXT_PUBLIC_REQUEST_TIMEOUT_MS` is absent
 * or unusable. `apps/work-permit/.env` sets it, so this covers a build that does
 * not load that file.
 *
 * Axios waits indefinitely without one, and an unanswered request then leaves
 * its promise unsettled: the screen keeps a spinner with no error and no retry.
 * A timeout classifies as `timeout`, which is retried for idempotent methods and
 * renders `ConnectionErrorFallback` for the rest — both recoverable. Deliberately
 * looser than any environment should configure, since it only applies when
 * configuration is missing.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 60_000;

/**
 * `Number(undefined)` is `NaN` and `Number('')` is `0`, and axios reads either
 * as "wait forever", so both must fall back rather than pass through.
 */
export function resolveRequestTimeoutMs(
  configured: string | undefined,
): number {
  const parsed = Number(configured);
  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : DEFAULT_REQUEST_TIMEOUT_MS;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

const defaultResponseTransforms = [
  axios.defaults.transformResponse ?? [],
].flat();

/**
 * Parses a JSON response body, failing when it does not parse.
 *
 * Axios parses JSON leniently: a body that does not parse is handed back as the
 * raw text. A mobile connection that drops mid-response delivers a 200 whose
 * JSON is cut off, and callers then read their fields off a string. Failing it
 * as `ERR_NETWORK`, with no response attached, lets the response interceptor
 * retry it like any other dropped connection. Other bodies keep axios's
 * default handling, as do error responses: axios runs this transform on those
 * too, and a throw there would replace the rejection and lose its status (a
 * 401 behind an HTML gateway page would be retried as a dropped connection).
 */
export const parseJsonBodyStrictly: AxiosResponseTransformer = function (
  this: InternalAxiosRequestConfig,
  data: unknown,
  headers: AxiosResponseHeaders | RawAxiosResponseHeaders,
  status?: number,
) {
  const contentType = String(headers?.['content-type'] ?? '');
  // Mirrors axios's `settle`: only a response that would resolve is checked.
  const resolves =
    !status || !this.validateStatus || this.validateStatus(status);
  if (
    !resolves ||
    typeof data !== 'string' ||
    data === '' ||
    !contentType.includes('application/json')
  ) {
    return defaultResponseTransforms.reduce(
      (body, transform) =>
        transform.call(this, body, headers as AxiosResponseHeaders, status),
      data,
    );
  }
  try {
    return JSON.parse(data);
  } catch {
    throw new AxiosError(
      'Response body is not valid JSON',
      AxiosError.ERR_NETWORK,
      this,
    );
  }
};

const httpClient = axios.create({
  timeout: resolveRequestTimeoutMs(REQUEST_TIMEOUT_MS),
  transformResponse: parseJsonBodyStrictly,
});

httpClient.interceptors.request.use((config) => {
  const withMetadata = withRequestMetadata(config);
  return applyDuplicateRequestCancellation(withMetadata);
});

httpClient.interceptors.response.use(
  (response) => {
    releaseInFlightRequest(response.config);
    if (hasRecentNetworkFailures()) {
      recordNetworkSuccess();
    }
    return response;
  },
  async (error: AxiosError) => {
    releaseInFlightRequest(error.config);

    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      throw error;
    }

    const config = error.config;

    if (!config) {
      if (shouldRecordNetworkFailure(error)) {
        recordNetworkFailure();
      }
      throw error;
    }

    const configWithMetadata = readRequestMetadata(config)
      ? config
      : withRequestMetadata(config);

    if (shouldRetryTransportRequest(configWithMetadata, error)) {
      const retryCount =
        (readRequestMetadata(configWithMetadata)?.retryCount ?? 0) + 1;
      const nextConfig = withUpdatedRetryMetadata(
        configWithMetadata,
        retryCount,
      );

      await sleep(getTransportRetryDelay(retryCount - 1));
      return httpClient(nextConfig);
    }

    if (shouldRecordNetworkFailure(error)) {
      recordNetworkFailure();
    }
    throw error;
  },
);

export type FetchDataOptions = {
  apiUrl: string;
  pathParams?: Record<string, string | number>;
  searchParams?: Record<string, string | number | boolean>;
  body?: unknown;
  disableCache?: boolean;
} & AxiosFetchRequestConfig;

function resolveRequestMethod(
  method: AxiosFetchRequestConfig['method'] | undefined,
  body: unknown,
): NonNullable<AxiosFetchRequestConfig['method']> {
  if (method) {
    return method;
  }
  return body ? 'post' : 'get';
}

/**
 * Central HTTP client for app and workflow API calls.
 * Includes transport retries, duplicate-request cancellation, and normalized errors.
 */
export const axiosFetch = async <T = unknown>({
  apiUrl,
  searchParams,
  body,
  disableCache,
  enableRetry,
  cancelDuplicate,
  method,
  ...options
}: FetchDataOptions): Promise<T> => {
  try {
    const headers = disableCache
      ? { ...options.headers, 'Cache-Control': 'no-cache' }
      : options.headers;

    const requestConfig = toAxiosConfigWithExtensions({
      method: resolveRequestMethod(method, body),
      url: apiUrl,
      params: searchParams,
      data: body,
      headers,
      enableRetry,
      cancelDuplicate,
      ...options,
    });

    const response = await httpClient<T>(requestConfig);

    return response.data;
  } catch (error) {
    // NOTE: Don't report errors here - they're already reported by baseSafeRouteHandler
    // This is just a transport layer, error reporting happens at the API route handler level

    // Parses InSafe numeric codes and external string codes (e.g. SAP `"401"`) via handleAxiosError
    if (axios.isCancel(error)) {
      throw error;
    }
    if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
      throw error;
    }
    return handleAxiosError(error, apiUrl);
  }
};
